/**
 * THE PAGE A CLIENT OPENS FROM A LINK. No sign-in and no navigation.
 *
 * The token in the URL is the whole credential, and it opens exactly one artifact: an invoice, or a
 * submitted visit report. It is not a client login (CLAUDE.md binding rule 5): there is no account,
 * no password, no session, and nothing behind this page changes the household, the visit or the
 * invoice's figures. When the link has expired or been withdrawn the server says so in a sentence,
 * and this page shows that sentence.
 *
 * AN INVOICE SHE OWES IS PAID FROM HERE (ops #46), one way to pay per screen, in the order the owner
 * decided on 2026-09-18: Zelle, which costs the business nothing; then a bank account through Stripe;
 * then a card, Apple Pay, Google Pay or Link through Stripe. Each screen is one tap from the next and
 * goes back to the one before, and every screen shows the same amount, because the published price
 * is the price however she pays (binding rule 3): the nudge is words and order, and nothing else.
 *
 * Zelle names the recipient from the one stored code, decoded on the server into the read: on a wide
 * screen the code is drawn for her phone to scan, on a narrow one the tag is there to copy, each a tap
 * from the other, and Zelle's own page is a plain link beside them. Stripe is asked for a page only
 * when she presses for one, through the one request behind a link that is not a read
 * (`POST /api/public/:token/pay-page`); the page is made after the answer has gone, so this asks
 * again a few times at a short interval and then sends her to Stripe in this same tab. An answer
 * that lands after she has moved on is dropped. A bank payment on its way refuses a second page, and
 * the page says so.
 *
 * ABOVE THE WAYS TO PAY IT ASKS "HOW DID WE DO?" (ops #46, step 2): four faces, the question the
 * invoice message asked. A good face goes with one tap; one below happy first asks which part of the
 * home and one line, both hers to leave empty, then goes. It is the one other write behind her link
 * (`POST /api/public/:token/feedback`), and what she said last is lit when she comes back, which she
 * can change with another tap. It goes to the office and is never published: an answer below happy
 * opens an issue and a follow-up there, and holds the invoice until a person has looked.
 *
 * A HOUSEHOLD WHOSE SAVED METHOD PAYS THIS INVOICE (ops #46, step 3b) sees no waterfall. The read
 * carries the method's words when she saved it before this invoice was issued, and the page says her
 * method is charged after she tells us the clean went well, during the business hours an owner set,
 * with a receipt by email only where one goes, and no bank or card screen, which would have her pay
 * twice. Zelle in the charge's place begins with one button, "I'm paying by Zelle" (the
 * owner, 2026-09-23), which pauses the charge of this invoice on the server before the page shows her
 * where to send it: a Zelle sent without it could be charged over by the next run.
 *
 * WHAT THE STAGING WALK OF 2026-09-23 CHANGED. Stripe's bank page draws no save box, so the bank screen
 * offers her own tick to save the account she pays from, unticked, with the paragraph beside it, while
 * nothing is saved for the household (`pay.offerToSave`); her ask carries it and the server honours it
 * only while nothing stands. After a face below happy the page says once, beside her answer, that the
 * office will be in touch, and the ways to pay wait behind a quiet "Ways to pay this invoice", closed
 * until she opens it, a rank quieter once open (and its review). The four faces are two to a row on a
 * phone, and the memo is in the page's own face.
 *
 * THE FUNNEL IS WRITTEN BY THE READS. The page loads naming the Zelle screen, and each move forward
 * names the screen it is about to show, so the server stamps the first time each was shown; the help
 * is named the first time it opens in a visit. A move back names nothing and asks nothing: the Zelle
 * screen named again would count another open. The QR code is drawn here from the stored URL by a
 * bundled encoder, so the page loads no script and no image from anywhere else.
 *
 * App.tsx renders this BEFORE asking who is signed in, because the person holding the link is not.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  AUTOPAY_BANK_TERMS,
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_E164,
  DOCUMENT_BEING_MADE,
  DOCUMENT_PAGE_WORDS,
  FEEDBACK_AREA_MAX,
  FEEDBACK_FACES,
  FEEDBACK_LINE_MAX,
  GOOD_FACES,
  type FeedbackFace,
  type HerChargeStage,
  type LinkPayPage,
} from '@fk/shared';
import {
  ApiError,
  askForDocument,
  askForPayPage,
  openPublic,
  sayPayingByZelle,
  tellUsHowItWent,
  type FeedbackBody,
  type FeedbackRecordedOnLink,
  type PublicArtifact,
  type PublicAutopay,
  type PublicDocument,
  type PublicFeedback,
  type PublicPay,
} from '../api.ts';
import { Button, TextAreaField, TextField } from '../components/Field.tsx';
import { Busy, ErrorNotice } from '../components/Notice.tsx';
import { PublicFrame } from '../components/PublicFrame.tsx';
import { QrCode } from '../components/QrCode.tsx';
import {
  businessHoursWords,
  formatDay,
  formatInstant,
  longDay,
  money,
  savedMethodNoun,
  savedMethodWords,
} from '../format.ts';
import { FEEDBACK_FACE_LABELS, INVOICE_STATE_LABELS } from '../labels.ts';
import { ZELLE_STUCK, chargedWords, zelleSteps } from '../pay-words.ts';

type InvoiceArtifact = Extract<PublicArtifact, { kind: 'invoice' }>;

/**
 * STRIPE MAKES THE PAGE AFTER THE ANSWER HAS GONE, so the first answer is often "being made". The
 * page asks again this long after each such answer, and this many times after the first, before it
 * says Stripe is slow and gives the button back: the cadence the office's pay link used before ops
 * #46, a few asks a few seconds apart and never a flood.
 */
export const PAY_PAGE_ASK_AGAIN_MS = 3000;
export const PAY_PAGE_ASKS = 4;

/**
 * Where the page sends her once Stripe's page exists: this tab, to Stripe's page, and the "Go to
 * Stripe" link beside it should anything stop that. One door, so a test can stand at it: a browser
 * cannot be navigated inside the test's document. Opening into this same tab makes no new window, so
 * no pop-up blocker stands in the way however long the page took to make.
 */
export const stripeDoor = {
  go(url: string): void {
    window.open(url, '_self');
  },
};

/**
 * Where the page sends her once the link to a PDF is minted: this tab, to the one route for files,
 * which answers the PDF inline, so a phone opens it and the back arrow returns to her page. One door,
 * as Stripe's is, so a test can stand at it, and opened as Stripe's is, into this same tab: no new
 * window, so no pop-up blocker stands in the way however long the link took to mint.
 */
export const documentDoor = {
  go(url: string): void {
    window.open(url, '_self');
  },
};

/** Wide enough to be a desktop, where a phone scans the code off the screen. Tailwind's `md`. */
const WIDE = '(min-width: 768px)';

function isWide(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(WIDE).matches;
}

export function PublicPage() {
  const { token = '' } = useParams();
  const [artifact, setArtifact] = useState<PublicArtifact | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    // The load names the first screen of the pay page, which the server stamps while she owes it.
    openPublic(token, 'zelle')
      .then((answer) => {
        if (live) setArtifact(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The page did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [token, attempt]);

  return (
    <PublicFrame household={artifact?.household}>
      {error ? (
        <ErrorNotice title="This link did not open" message={error.message} onRetry={retry} />
      ) : artifact === null ? (
        <Busy what="your page" />
      ) : artifact.kind === 'invoice' ? (
        <Invoice token={token} artifact={artifact} onFresh={setArtifact} />
      ) : (
        <Report artifact={artifact} />
      )}
    </PublicFrame>
  );
}

function Invoice({
  token,
  artifact,
  onFresh,
}: {
  token: string;
  artifact: InvoiceArtifact;
  onFresh: (fresh: PublicArtifact) => void;
}) {
  /** What she said last, which is what a saved method's charge waits on. */
  const [latest, setLatest] = useState<FeedbackFace | null>(artifact.feedback.latestFace);
  // As the read said it when the page loaded, and then as each tap's answer says it: a So-so naming
  // the kitchen holds the charge, and a Happy after it on the same page does not lift the hold (the
  // review of ops #72's fix).
  const [waits, setWaits] = useState(artifact.waitsOnPuttingRight);
  return (
    <>
      <section className="rounded-xl border border-navy/12 bg-parchment p-5">
        <h1 className="font-serif text-2xl leading-tight">Your invoice</h1>
        {artifact.visitDate ? (
          <p className="mt-1 text-sm text-navy/70">
            For the visit on {longDay(artifact.visitDate)}
          </p>
        ) : null}
        <ul className="mt-4 divide-y divide-navy/10">
          {artifact.lines.map((line) => (
            <li key={line.position} className="flex justify-between gap-3 py-2 text-base">
              <span>
                {line.description}
                {line.quantity !== 1 ? (
                  <span className="text-navy/60">
                    {' '}
                    x{line.quantity} at {money(line.unitPriceCents)}
                  </span>
                ) : null}
              </span>
              <span className="whitespace-nowrap">{money(line.amountCents)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-between border-t border-navy/20 pt-2 text-lg">
          <span className="font-medium">Total</span>
          <span className="font-medium">{money(artifact.totalCents)}</span>
        </div>
        <div className="flex justify-between text-lg">
          <span>Still owed</span>
          <span className={artifact.balanceCents > 0 ? 'font-semibold' : ''}>
            {money(artifact.balanceCents)}
          </span>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-2 text-sm">
          {artifact.issuedOn ? (
            <div>
              <dt className="text-xs tracking-wide text-navy/55 uppercase">Issued</dt>
              <dd>{formatDay(artifact.issuedOn)}</dd>
            </div>
          ) : null}
          {artifact.dueOn ? (
            <div>
              <dt className="text-xs tracking-wide text-navy/55 uppercase">Due</dt>
              <dd>{formatDay(artifact.dueOn)}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-xs tracking-wide text-navy/55 uppercase">Standing</dt>
            <dd>{INVOICE_STATE_LABELS[artifact.state]}</dd>
          </div>
        </dl>
        {artifact.pay.bankPaymentClearing ? (
          <p className="mt-4 rounded-lg border border-gold/50 bg-gold/10 px-3 py-2 text-sm">
            A bank payment for this invoice is on its way. There is nothing to pay here while it
            clears.
          </p>
        ) : null}
        {artifact.charged ? (
          <p className="mt-4 rounded-lg border border-navy/15 bg-cream px-3 py-2 text-sm">
            {chargedWords(artifact.charged)}
          </p>
        ) : null}
        <p className="mt-5 text-sm text-navy/70">
          Thank you. If anything on this page looks wrong, call or text the office and we will put
          it right.
        </p>
      </section>
      <Documents token={token} documents={artifact.documents} />
      <HowDidWeDo
        token={token}
        feedback={artifact.feedback}
        onLatest={(answer) => {
          setLatest(answer.latestFace);
          setWaits(answer.waitsOnPuttingRight);
        }}
      />
      {!artifact.pay.payable ? null : artifact.autopay ? (
        <SavedMethodPays
          token={token}
          pay={artifact.pay}
          autopay={artifact.autopay}
          amountCents={artifact.balanceCents}
          latest={latest}
          waitsOnPuttingRight={waits}
          onFresh={onFresh}
        />
      ) : (
        <PayWaterfall
          token={token}
          pay={artifact.pay}
          amountCents={artifact.balanceCents}
          latest={latest}
          onFresh={onFresh}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Her invoice and its receipts, as PDFs (ops #76 O8).
 * ------------------------------------------------------------------ */

/**
 * Where her press for a PDF stands: nothing yet, being answered, or why it opened nothing. Kept for
 * one document at a time, the one she pressed last.
 */
type DocumentStep =
  | { readonly at: 'idle' }
  | { readonly at: 'getting' | 'not_ready' | 'not_got'; readonly documentId: string };

/** The kinds her page offers: the invoice's own PDF and its receipts. A cancellation is never hers. */
const OFFERED_HERE: ReadonlySet<PublicDocument['kind']> = new Set(['invoice', 'receipt']);

/**
 * HER INVOICE'S DOCUMENTS (ops #76 O8, `docs/seams/home.md` section 11.2), under the invoice: "Download
 * the invoice (PDF)" and one button for each receipt of a payment applied to it. A press asks the
 * server for a link minted then, ten minutes of that one PDF (`POST /api/public/:token/document`),
 * and opens it in this same tab, as Stripe's page is opened; the PDF opens inline and the back arrow
 * returns here. A press is not an open and writes nothing, so the page is not read again.
 *
 * A PDF still being made shows the line home shows in its place, and no button; a press the server
 * answers so says the same. Any other refusal, or no answer at all, says it could not get the PDF
 * just now, in home's words, and leaves the button to press again: never the server's own sentence,
 * which is written for home and the office. An invoice sent before the PDFs began has none, and the
 * block is not drawn.
 */
function Documents({ token, documents }: { token: string; documents: readonly PublicDocument[] }) {
  const [step, setStep] = useState<DocumentStep>({ at: 'idle' });
  const showing = useRef(true);

  useEffect(() => {
    showing.current = true;
    return () => {
      showing.current = false;
    };
  }, []);

  const offered = documents.filter((document) => OFFERED_HERE.has(document.kind));
  if (offered.length === 0) return null;

  async function open(documentId: string) {
    setStep({ at: 'getting', documentId });
    try {
      const link = await askForDocument(token, documentId);
      if (!showing.current) return;
      setStep({ at: 'idle' });
      documentDoor.go(link.url);
    } catch (thrown) {
      if (!showing.current) return;
      const notReady = thrown instanceof ApiError && thrown.code === 'document_not_ready';
      setStep({ at: notReady ? 'not_ready' : 'not_got', documentId });
    }
  }

  const getting = step.at === 'getting';

  return (
    <section
      aria-label={DOCUMENT_PAGE_WORDS.heading}
      className="mt-4 rounded-xl border border-navy/12 bg-parchment p-5"
    >
      <h2 className="font-serif text-2xl leading-tight">{DOCUMENT_PAGE_WORDS.heading}</h2>
      <ul className="mt-3 space-y-3">
        {offered.map((document) => {
          const pressed = step.at !== 'idle' && step.documentId === document.documentId;
          const label =
            document.kind === 'receipt'
              ? DOCUMENT_PAGE_WORDS.receipt(document.number)
              : DOCUMENT_PAGE_WORDS.invoice;
          return (
            <li key={document.documentId}>
              {!document.ready || (pressed && step.at === 'not_ready') ? (
                <p className="text-sm">{DOCUMENT_BEING_MADE}</p>
              ) : (
                <>
                  <Button
                    tone="quiet"
                    disabled={getting}
                    onClick={() => void open(document.documentId)}
                  >
                    {pressed && getting ? DOCUMENT_PAGE_WORDS.getting : label}
                  </Button>
                  {pressed && step.at === 'not_got' ? (
                    <p className="mt-2 text-sm" role="alert">
                      {DOCUMENT_PAGE_WORDS.notGot}
                    </p>
                  ) : null}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * How did we do: four faces, above the ways to pay.
 * ------------------------------------------------------------------ */

const isGood = (face: FeedbackFace) => (GOOD_FACES as readonly string[]).includes(face);

/**
 * WHAT THE PAGE SAYS ONCE SHE HAS TOLD US SOMETHING WAS NOT RIGHT, and it says it once, here, beside
 * what she told us: the review of the staging walk of 2026-09-23 found it said three times in a row,
 * here, as the heading of the ways to pay and again in the line beneath it.
 */
const IN_TOUCH = 'The office will be in touch to put it right.';

/** The words after an answer, one for a good face and one for a face below happy. */
const GOOD_THANKS = 'Thank you for telling us.';
const SAD_THANKS = `Thank you for telling us. ${IN_TOUCH}`;

/** Where a tap stands: nothing yet, on its way, or answered with one of the lines above. */
type TellStep =
  | { readonly at: 'idle' | 'sending' }
  | { readonly at: 'told'; readonly words: string }
  | { readonly at: 'failed'; readonly message: string };

function HowDidWeDo({
  token,
  feedback,
  onLatest,
}: {
  token: string;
  feedback: PublicFeedback;
  /**
   * What she said last and whether the charge waits on putting it right, once an answer has gone, for
   * the part of the page that waits on them.
   */
  onLatest: (answer: FeedbackRecordedOnLink) => void;
}) {
  const [latest, setLatest] = useState<FeedbackFace | null>(feedback.latestFace);
  /** A face below happy she tapped, waiting for the part of the home and a line, or for nothing. */
  const [choosing, setChoosing] = useState<FeedbackFace | null>(null);
  const [area, setArea] = useState('');
  const [line, setLine] = useState('');
  const [step, setStep] = useState<TellStep>({ at: 'idle' });

  async function tell(face: FeedbackFace) {
    // What she typed goes with the face below happy whose form she typed it in, and never with a good
    // face she tapped instead, which closes that form: a part of the home sent with a good face opens
    // an issue and holds the charge (ops #72), and she would never have seen it go.
    const typedArea = isGood(face) ? '' : area.trim();
    const typedLine = isGood(face) ? '' : line.trim();
    const body: FeedbackBody = {
      face,
      ...(typedArea ? { area: typedArea } : {}),
      ...(typedLine ? { line: typedLine } : {}),
    };
    if (typedArea.length > FEEDBACK_AREA_MAX || typedLine.length > FEEDBACK_LINE_MAX) {
      setStep({
        at: 'failed',
        message: `The part of the home can be ${FEEDBACK_AREA_MAX} characters and the line ${FEEDBACK_LINE_MAX}. Shorten them a little.`,
      });
      return;
    }
    setStep({ at: 'sending' });
    try {
      const answer = await tellUsHowItWent(token, body);
      setLatest(answer.latestFace);
      onLatest(answer);
      setChoosing(null);
      setArea('');
      setLine('');
      setStep({
        at: 'told',
        words: !answer.recorded
          ? `This link has told us all it can. Call or text the office at ${BUSINESS_PHONE_DISPLAY} to say more.`
          : isGood(face)
            ? GOOD_THANKS
            : SAD_THANKS,
      });
    } catch (thrown) {
      setStep({
        at: 'failed',
        message: thrown instanceof ApiError ? thrown.message : 'Your answer did not reach us.',
      });
    }
  }

  function tap(face: FeedbackFace) {
    if (isGood(face)) {
      setChoosing(null);
      void tell(face);
    } else {
      setChoosing(face);
      setStep({ at: 'idle' });
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (choosing) void tell(choosing);
  }

  const shown = choosing ?? latest;
  const sending = step.at === 'sending';

  return (
    <section
      aria-label="How did we do?"
      className="mt-4 rounded-xl border border-navy/12 bg-parchment p-5"
    >
      <h2 className="font-serif text-2xl leading-tight">How did we do?</h2>
      <p className="mt-1 text-sm text-navy/70">
        Tap the face that fits. Your answer goes to the office and nowhere else.
      </p>
      {/* Two to a row on a phone, where four left "Unhappy" filling its button (the staging walk of
          2026-09-23), and four in a row once the screen is wide enough for them. */}
      <div
        className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4"
        role="group"
        aria-label="How the clean went"
      >
        {FEEDBACK_FACES.map((face) => (
          <button
            key={face}
            type="button"
            aria-pressed={shown === face}
            disabled={sending}
            onClick={() => tap(face)}
            className={[
              'flex min-h-11 flex-col items-center gap-1 rounded-lg border px-1 py-2',
              shown === face ? 'border-navy bg-navy text-cream' : 'border-navy/25 bg-cream',
            ].join(' ')}
          >
            <Face face={face} />
            <span className="text-sm font-medium">{FEEDBACK_FACE_LABELS[face]}</span>
          </button>
        ))}
      </div>

      {choosing ? (
        <form className="mt-4" onSubmit={submit} noValidate>
          <TextField
            id="feedback-area"
            label="Which part of the home?"
            value={area}
            onChange={setArea}
            autoComplete="off"
            hint="The kitchen, a bathroom, the floors. Leave it empty if it was not one place."
          />
          <TextAreaField
            id="feedback-line"
            label="Anything you want us to know?"
            value={line}
            onChange={setLine}
            rows={3}
          />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Button type="submit" disabled={sending}>
              {sending ? 'One moment' : 'Tell us'}
            </Button>
            <button
              type="button"
              className={LINK_BUTTON}
              disabled={sending}
              onClick={() => {
                setChoosing(null);
                setStep({ at: 'idle' });
              }}
            >
              Never mind
            </button>
          </div>
        </form>
      ) : null}

      {step.at === 'told' ? (
        <p className="mt-3 text-sm" role="status">
          {step.words}
        </p>
      ) : latest && !choosing ? (
        <p className="mt-3 text-sm">
          You told us: {FEEDBACK_FACE_LABELS[latest]}.{isGood(latest) ? '' : ` ${IN_TOUCH}`} Tap
          another face to change it.
        </p>
      ) : null}
      {step.at === 'failed' ? (
        <div className="mt-3">
          <ErrorNotice title="Your answer did not go" message={step.message} />
        </div>
      ) : null}
    </section>
  );
}

/** A face drawn in a few strokes: two eyes and a mouth that turns down as the answer does. */
function Face({ face }: { face: FeedbackFace }) {
  const mouth: Record<FeedbackFace, string> = {
    delighted: 'M8 14 Q12 19 16 14 Z',
    happy: 'M8 14 Q12 17.5 16 14',
    so_so: 'M8.5 15 L15.5 15',
    unhappy: 'M8 16.5 Q12 13 16 16.5',
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={32}
      height={32}
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="9" cy="9.5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9.5" r="0.9" fill="currentColor" stroke="none" />
      <path d={mouth[face]} fill={face === 'delighted' ? 'currentColor' : 'none'} />
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * The waterfall: Zelle, then a bank account, then a card.
 * ------------------------------------------------------------------ */

type PayScreenShown = 'zelle' | 'bank' | 'card';

/** Where a press for one of Stripe's pages stands, from the press to Stripe, or to why not. */
type AskStep =
  | { readonly at: 'idle' | 'asking' | 'making' | 'slow' }
  | { readonly at: 'going'; readonly url: string }
  /** Asking again cannot help: nothing owed, a bank payment clearing, or no page from this link. */
  | { readonly at: 'stopped'; readonly message: string }
  /** It might, once the signal is back. */
  | { readonly at: 'failed'; readonly message: string };

const OFFICE_LINE = `Call or text the office at ${BUSINESS_PHONE_DISPLAY}.`;

/**
 * Said on both of Stripe's screens, because the order of the screens is a nudge and must never read
 * as a price (binding rule 3): the published price is the price however she pays.
 */
const SAME_PRICE = 'The price is the same however you pay.';

/**
 * THE WAYS TO PAY ONCE SHE HAS TOLD US SOMETHING WAS NOT RIGHT (the staging walk of 2026-09-23 and its
 * review): the page led with "Zelle is the easy one." after a so-so, and then, beneath the office being
 * in touch, the Zelle code still filled most of it. The office being in touch is said once, beside her
 * answer (`IN_TOUCH`), and the ways to pay wait behind this one quiet control, closed until she opens
 * it, each screen a rank quieter once open.
 */
const WAYS_TO_PAY = 'Ways to pay this invoice';

/** The heading of one screen of the waterfall: the first thing she reads, or a rank below it. */
function ScreenHeading({ quiet, children }: { quiet: boolean; children: ReactNode }) {
  return quiet ? (
    <h3 className="font-serif text-lg leading-tight">{children}</h3>
  ) : (
    <h2 className="font-serif text-2xl leading-tight">{children}</h2>
  );
}

/**
 * HER OWN TICK ON THE BANK SCREEN (ops #46, the staging walk of 2026-09-23). Stripe's page for a bank
 * account alone draws no save box, so the bank screen offers one of ours, unticked until she ticks it,
 * with the paragraph she agrees to beside it, about her bank account as the tick is, the words her
 * saved account is recorded against (`AUTOPAY_BANK_TERMS`); Stripe's bank page then confirms what she
 * chose in two sentences. Ticked, her ask for the bank page carries it and the page saves the account
 * she pays from; unticked, the page saves nothing and says nothing about saving. Offered only where
 * the read says so, which is while nothing is saved for the household.
 */
function SaveAccountTick({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-navy/15 bg-cream p-3">
      <div className="flex items-start gap-3">
        <input
          id="save-account"
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.currentTarget.checked)}
          aria-describedby="save-account-terms"
          className="mt-1 h-5 w-5 shrink-0 accent-navy"
        />
        <label htmlFor="save-account" className="text-base font-medium">
          Charge my bank account automatically after each clean
        </label>
      </div>
      <p id="save-account-terms" className="mt-2 text-sm text-navy/75">
        {AUTOPAY_BANK_TERMS}
      </p>
    </div>
  );
}

function PayWaterfall({
  token,
  pay,
  amountCents,
  latest,
  onFresh,
}: {
  token: string;
  pay: PublicPay;
  amountCents: number;
  /** What she said last, which puts the office being in touch first when it was below happy. */
  latest: FeedbackFace | null;
  onFresh: (fresh: PublicArtifact) => void;
}) {
  const [screen, setScreen] = useState<PayScreenShown>('zelle');
  const [step, setStep] = useState<AskStep>({ at: 'idle' });
  /** Her own tick on the bank screen, unticked until she ticks it, kept while she moves between them. */
  const [saveAccount, setSaveAccount] = useState(false);
  const told = latest !== null && !isGood(latest);
  /** Whether she opened the ways to pay after telling us something was not right. */
  const [open, setOpen] = useState(false);
  const showing = useRef(true);
  /**
   * Whether this visit has named the help yet. Kept here rather than on the Zelle screen, which is
   * drawn afresh each time she comes back to it, so the help is named once however often she goes.
   */
  const helpNamed = useRef(false);
  const again = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * The press now being answered. Every press, and every move between screens, takes the next
   * number, so an answer to an earlier press that lands after she moved on is dropped.
   */
  const pressNo = useRef(0);

  const stopAsking = useCallback(() => {
    if (again.current !== null) clearTimeout(again.current);
    again.current = null;
    pressNo.current += 1;
  }, []);

  useEffect(() => {
    showing.current = true;
    return () => {
      showing.current = false;
      stopAsking();
    };
  }, [stopAsking]);

  // A face below happy closes the ways to pay, given before the page loaded or just now; she opens
  // them again when she wants them.
  useEffect(() => {
    if (told) setOpen(false);
  }, [told]);

  /**
   * THE ZELLE SCREEN IS NAMED ONCE, WHEN IT IS FIRST SHOWN (ops #54, item 10). The load names it, and
   * the server stamps it only where the load shows it: not after an answer below happy, when the ways
   * to pay load folded away. So a page that loaded folded names it the first time it is shown, when
   * she opens them or a good face does, as a move that is never another open; a page that loaded on
   * it has named it already.
   */
  const zelleNamed = useRef(!told);
  const zelleShown = (!told || open) && screen === 'zelle';
  useEffect(() => {
    if (!zelleShown || zelleNamed.current) return;
    zelleNamed.current = true;
    openPublic(token, 'zelle', { unfolded: true })
      .then((fresh) => {
        if (showing.current) onFresh(fresh);
      })
      // The stamp is measurement; a failed one costs her nothing.
      .catch(() => {});
  }, [zelleShown, token, onFresh]);

  /** Opens or closes the ways to pay; closing stops any ask for a page she can no longer see. */
  function toggleWays() {
    if (open) {
      stopAsking();
      setStep({ at: 'idle' });
    }
    setOpen(!open);
  }

  const amount = money(amountCents);

  /** A move forward names the screen, so the server stamps it the first time; the answer refreshes. */
  function forwardTo(next: 'bank' | 'card') {
    stopAsking();
    setStep({ at: 'idle' });
    setScreen(next);
    openPublic(token, next)
      .then((fresh) => {
        if (showing.current) onFresh(fresh);
      })
      // The stamp is measurement; a failed one costs her nothing, and the press says what is wrong.
      .catch(() => {});
  }

  /** A move back asks nothing: the screen was stamped on the way forward. */
  function backTo(previous: 'zelle' | 'bank') {
    stopAsking();
    setStep({ at: 'idle' });
    setScreen(previous);
  }

  // `no` is the press this ask answers; `asked` is how many times it has already asked again, 0 for
  // the press itself; `saving` is her tick as it stood when she pressed, which every ask again of the
  // same press carries. An answer that arrives after the page or the press has gone is dropped.
  async function ask(page: LinkPayPage, no: number, asked: number, saving: boolean) {
    again.current = null;
    try {
      const answer = await askForPayPage(token, page, saving);
      if (!showing.current || pressNo.current !== no) return;
      if (answer.state === 'ready') {
        setStep({ at: 'going', url: answer.url });
        stripeDoor.go(answer.url);
      } else if (asked >= PAY_PAGE_ASKS) {
        setStep({ at: 'slow' });
      } else {
        setStep({ at: 'making' });
        again.current = setTimeout(
          () => void ask(page, no, asked + 1, saving),
          PAY_PAGE_ASK_AGAIN_MS,
        );
      }
    } catch (thrown) {
      if (!showing.current || pressNo.current !== no) return;
      if (thrown instanceof ApiError && thrown.status === 404) {
        // A link withdrawn or run out since the page loaded, or Stripe switched off here.
        setStep({
          at: 'stopped',
          message: `This way to pay is not open from this link now. ${OFFICE_LINE}`,
        });
      } else if (thrown instanceof ApiError && (thrown.status === 409 || thrown.status === 400)) {
        // A bank payment on its way, or nothing owed now: the server's sentence is written for her.
        setStep({ at: 'stopped', message: thrown.message });
      } else {
        setStep({
          at: 'failed',
          message: thrown instanceof ApiError ? thrown.message : 'Stripe did not answer.',
        });
      }
    }
  }

  function press(page: LinkPayPage) {
    stopAsking();
    setStep({ at: 'asking' });
    void ask(page, pressNo.current, 0, page === 'bank' && pay.offerToSave && saveAccount);
  }

  /**
   * A change of her mind about saving stops any ask still going for the page she no longer wants, and
   * gives the button back, so the next press asks as she has left the tick.
   */
  function changeTick(checked: boolean) {
    stopAsking();
    setStep({ at: 'idle' });
    setSaveAccount(checked);
  }

  return (
    <section
      aria-label="How to pay"
      className="mt-4 rounded-xl border border-navy/12 bg-parchment p-5"
    >
      {told ? (
        <button
          type="button"
          className={LINK_BUTTON}
          aria-expanded={open}
          aria-controls="ways-to-pay"
          onClick={toggleWays}
        >
          {WAYS_TO_PAY}
        </button>
      ) : null}
      {told && !open ? null : (
        <div id="ways-to-pay" className={told ? 'mt-3' : undefined}>
          {screen === 'zelle' ? (
            <ZelleScreen
              token={token}
              pay={pay}
              amount={amount}
              onFresh={onFresh}
              showing={showing}
              helpNamed={helpNamed}
              quiet={told}
              onAnotherWay={pay.online ? () => forwardTo('bank') : null}
            />
          ) : screen === 'bank' ? (
            <StripeScreen
              headline="Your bank account is next best."
              line={`Pay ${amount} from your checking or savings account through Stripe. It costs us far less than a card. ${SAME_PRICE}`}
              quiet={told}
              extra={
                pay.offerToSave ? (
                  <SaveAccountTick checked={saveAccount} onChange={changeTick} />
                ) : null
              }
              action="Pay from my bank account"
              step={step}
              onPress={() => press('bank')}
              secondary={{ words: "I can't pay by bank", onPress: () => forwardTo('card') }}
              back={{ words: 'Back to Zelle', onPress: () => backTo('zelle') }}
            />
          ) : (
            <StripeScreen
              headline="Card, Apple Pay or Google Pay."
              line={`Pay ${amount} through Stripe by card, Apple Pay, Google Pay or Link. ${SAME_PRICE}`}
              quiet={told}
              extra={null}
              action="Pay by card"
              step={step}
              onPress={() => press('card')}
              secondary={null}
              back={{ words: 'Back to bank account', onPress: () => backTo('bank') }}
            />
          )}
        </div>
      )}
    </section>
  );
}

const LINK_BUTTON =
  'inline-flex min-h-11 items-center text-sm font-medium underline underline-offset-2';

function ZelleScreen({
  token,
  pay,
  amount,
  onFresh,
  showing,
  helpNamed,
  quiet,
  onAnotherWay,
}: {
  token: string;
  pay: PublicPay;
  amount: string;
  onFresh: (fresh: PublicArtifact) => void;
  showing: { readonly current: boolean };
  /** Whether this visit has named the help to the server, which it does once. */
  helpNamed: { current: boolean };
  /** A rank quieter, beneath the office being in touch, once she said something was not right. */
  quiet: boolean;
  /** Null where this deployment takes no payment through Stripe, so there is no other screen. */
  onAnotherWay: (() => void) | null;
}) {
  const { tag, memo } = pay.zelle;
  const withMemo = memo ? `, memo ${memo}` : '';

  return (
    <div>
      <ScreenHeading quiet={quiet}>Zelle is the easy one.</ScreenHeading>
      <p className="mt-2 text-base">
        We are two people, not a chain, and Zelle costs us nothing: {amount} to{' '}
        <span className="font-semibold">{tag}</span>
        {withMemo}.
      </p>

      <ZelleDetails
        token={token}
        pay={pay}
        amount={amount}
        onFresh={onFresh}
        showing={showing}
        helpNamed={helpNamed}
      />

      <div className="mt-4 border-t border-navy/10 pt-4">
        {onAnotherWay ? (
          <Button tone="quiet" onClick={onAnotherWay}>
            Pay another way
          </Button>
        ) : (
          <p className="text-sm">
            Can&apos;t use Zelle? Call or text the office at {BUSINESS_PHONE_DISPLAY} to pay another
            way.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * HOW TO SEND IT BY ZELLE: the code to scan or the tag to copy, each a tap from the other, the amount
 * and the memo, Zelle's own page, and the four-line how-to with the office's number, named to the
 * server the first time it opens in a visit. The Zelle screen of the waterfall draws it, and so does
 * a saved method's page, where Zelle is offered in the charge's place.
 */
function ZelleDetails({
  token,
  pay,
  amount,
  onFresh,
  showing,
  helpNamed,
}: {
  token: string;
  pay: PublicPay;
  amount: string;
  onFresh: (fresh: PublicArtifact) => void;
  showing: { readonly current: boolean };
  /** Whether this visit has named the help to the server, which it does once. */
  helpNamed: { current: boolean };
}) {
  const { qrUrl, tag, name, memo } = pay.zelle;
  const [view, setView] = useState<'code' | 'tag'>(() => (isWide() ? 'code' : 'tag'));
  const [help, setHelp] = useState(false);
  const [copied, setCopied] = useState<boolean | null>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(tag);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  function toggleHelp() {
    const opening = !help;
    setHelp(opening);
    // The help is a screen of the funnel too, named the first time it opens and never again.
    if (opening && !helpNamed.current) {
      helpNamed.current = true;
      openPublic(token, 'zelle_help')
        .then((fresh) => {
          if (showing.current) onFresh(fresh);
        })
        .catch(() => {});
    }
  }

  return (
    <>
      <div className="mt-4 rounded-lg border border-navy/15 bg-cream p-4">
        {view === 'code' ? (
          <div>
            <QrCode text={qrUrl} label={`Zelle code for ${name}`} />
            <p className="mt-3 text-center text-sm">
              Scan it from the Zelle screen in your bank&apos;s app. It will show {name}.
            </p>
            <button type="button" className={LINK_BUTTON} onClick={() => setView('tag')}>
              Show the tag to copy
            </button>
          </div>
        ) : (
          <div>
            <dl className="grid grid-cols-1 gap-2 text-base">
              <div>
                <dt className="text-xs tracking-wide text-navy/55 uppercase">Send to</dt>
                <dd className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-lg select-all">{tag}</span>
                  <Button tone="quiet" onClick={() => void copy()}>
                    Copy
                  </Button>
                  {copied === true ? (
                    <span className="text-sm" role="status">
                      Copied.
                    </span>
                  ) : null}
                  {copied === false ? (
                    <span className="text-sm">
                      This browser would not copy it. Type it instead.
                    </span>
                  ) : null}
                </dd>
              </div>
              <div>
                <dt className="text-xs tracking-wide text-navy/55 uppercase">
                  Your bank will show
                </dt>
                <dd>{name}</dd>
              </div>
            </dl>
            <p className="mt-3 text-sm">
              Open your bank&apos;s app, choose Zelle, and paste {tag}.
            </p>
            <button type="button" className={LINK_BUTTON} onClick={() => setView('code')}>
              Show the code to scan
            </button>
          </div>
        )}
        <dl className="mt-3 grid grid-cols-2 gap-2 text-base">
          <div>
            <dt className="text-xs tracking-wide text-navy/55 uppercase">Amount</dt>
            <dd className="font-semibold">{amount}</dd>
          </div>
          {memo ? (
            <div>
              <dt className="text-xs tracking-wide text-navy/55 uppercase">Memo</dt>
              {/* In the page's own face: a typewriter face spread "FK Sep 22" into three words. */}
              <dd className="font-semibold whitespace-nowrap">{memo}</dd>
            </div>
          ) : null}
        </dl>
        <a href={qrUrl} target="_blank" rel="noopener noreferrer" className={`mt-2 ${LINK_BUTTON}`}>
          Open Zelle&apos;s page
        </a>
      </div>

      <div className="mt-4">
        <button type="button" className={LINK_BUTTON} aria-expanded={help} onClick={toggleHelp}>
          How do I use Zelle?
        </button>
        {help ? (
          <div className="mt-2 text-sm">
            <ol className="list-decimal space-y-1 pl-5">
              {zelleSteps({ tag, name, amount, memo }).map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p className="mt-2">
              {ZELLE_STUCK}{' '}
              <a href={`tel:${BUSINESS_PHONE_E164}`} className="font-medium underline">
                {BUSINESS_PHONE_DISPLAY}
              </a>
              .
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * A saved method pays this one.
 * ------------------------------------------------------------------ */

/**
 * HER SAVED METHOD PAYS THIS INVOICE (ops #46, step 3b). She ticked Stripe's save box on an earlier
 * page, and this invoice was issued since, so the server's read carries the method's words
 * (`autopay`). The page says which of hers is charged and on what: her word that the clean went well,
 * because silence never charges and the first charge waits on her answer alone. What it says follows
 * the face she gave last: before she answers, that it waits on her; after a good face, that it will
 * be charged; after one below happy, or a good one while the charge waits on putting right a missed
 * area she named (`waitsOnPuttingRight`, ops #72), that nothing is charged until what she told us is put
 * right, which is the paragraph she agreed to when she ticked to save it (`AUTOPAY_TERMS` for a card,
 * `AUTOPAY_BANK_TERMS` for a bank account).
 *
 * WHEN, AND THE RECEIPT (the staging walk of 2026-09-23): the page never said when her method is
 * charged, and told a payer with no email her receipt was emailed. It says the charge comes during the
 * business hours an owner set, which the read carries (`autopay.businessHours`) and the job charges
 * inside alone, and that a receipt is emailed only where one is (`autopay.receiptByEmail`): the
 * household's payer has an email the register lets ops write to.
 *
 * ZELLE IN THE CHARGE'S PLACE BEGINS WITH HER SAYING SO (the owner, 2026-09-23). "I'm paying by Zelle"
 * pauses the charge of this invoice on the server (`POST /api/public/:token/paying-by-zelle`), and only
 * then does the page show where to send it, the same code, tag, memo and how-to as the Zelle screen:
 * a Zelle sent before the pause could be charged over by the next run, before the office had recorded
 * it. While the pause stands the card is not charged; the office records the Zelle, which ends the
 * charge, or lifts the pause, and her link cannot pause it again, so the page then sends her to the
 * office first. Once the charge is at Stripe there is nothing to pause, and the page says it is being
 * charged. The words follow `autopay.stage`, and her press moves it here without reading the page
 * again, which would count another open.
 *
 * No bank or card screen, which would ask her to pay twice. The same amount, the balance, is the only
 * figure (binding rule 3). Nothing here asks Stripe for anything.
 */
function SavedMethodPays({
  token,
  pay,
  autopay,
  amountCents,
  latest,
  waitsOnPuttingRight,
  onFresh,
}: {
  token: string;
  pay: PublicPay;
  autopay: PublicAutopay;
  amountCents: number;
  latest: FeedbackFace | null;
  /** The charge held for an issue she raised, which a good face naming a missed area raises too. */
  waitsOnPuttingRight: boolean;
  onFresh: (fresh: PublicArtifact) => void;
}) {
  const showing = useRef(true);
  const helpNamed = useRef(false);
  const [stage, setStage] = useState<HerChargeStage>(autopay.stage);
  const [pressing, setPressing] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  useEffect(() => {
    showing.current = true;
    return () => {
      showing.current = false;
    };
  }, []);

  const amount = money(amountCents);
  const hers = savedMethodWords(autopay);
  const noun = savedMethodNoun(autopay.methodType);
  // When, from the business day an owner set, which the charge runs inside alone; and a receipt only
  // where one goes by email (the staging walk of 2026-09-23).
  const during = `during business hours: ${businessHoursWords(autopay.businessHours)}.`;
  const receipt = autopay.receiptByEmail ? ' A receipt is emailed when it is charged.' : '';
  // A good face that named a missed area holds the charge as one below happy does (ops #72), so the
  // page asks the read whether the charge waits on putting it right, not her face alone.
  const standing =
    latest === null
      ? `We will charge your ${hers} after you tell us the clean went well, ${during}${receipt}`
      : isGood(latest) && !waitsOnPuttingRight
        ? `Thank you. We will charge your ${hers} ${during}${receipt}`
        : `Nothing is charged to your ${hers} until what you told us is put right.`;

  async function payingByZelle() {
    setPressing(true);
    setRefused(null);
    try {
      const answer = await sayPayingByZelle(token);
      if (showing.current) setStage(answer.stage);
    } catch (thrown) {
      if (!showing.current) return;
      if (thrown instanceof ApiError && thrown.code === 'charge_in_flight') {
        setStage('at_stripe');
      } else if (thrown instanceof ApiError && thrown.code === 'pause_lifted') {
        setStage('pause_lifted');
      } else {
        setRefused(thrown instanceof ApiError ? thrown.message : 'That did not reach us.');
      }
    } finally {
      if (showing.current) setPressing(false);
    }
  }

  const frame = (headline: string, body: ReactNode) => (
    <section
      aria-label="How to pay"
      className="mt-4 rounded-xl border border-navy/12 bg-parchment p-5"
    >
      <h2 className="font-serif text-2xl leading-tight">{headline}</h2>
      {body}
    </section>
  );

  if (stage === 'paying_by_zelle') {
    return frame(
      'You are paying this one by Zelle.',
      <>
        <p className="mt-2 text-base">
          We will not charge your {hers} for this invoice while we wait for your Zelle. Send{' '}
          {amount} to <span className="font-semibold">{pay.zelle.tag}</span>
          {pay.zelle.memo ? ' with the memo below' : ''}. The office records it when it arrives.
        </p>
        <ZelleDetails
          token={token}
          pay={pay}
          amount={amount}
          onFresh={onFresh}
          showing={showing}
          helpNamed={helpNamed}
        />
        <p className="mt-4 text-sm">
          Changed your mind? Call or text the office at{' '}
          <a href={`tel:${BUSINESS_PHONE_E164}`} className="font-medium underline">
            {BUSINESS_PHONE_DISPLAY}
          </a>
          .
        </p>
      </>,
    );
  }

  if (stage === 'at_stripe') {
    return frame(
      `Your ${noun} is saved.`,
      <>
        <p className="mt-2 text-base">We are charging your {hers} for this invoice now.</p>
        <p className="mt-3 text-sm">
          If you have already sent a Zelle for it, call or text the office at{' '}
          <a href={`tel:${BUSINESS_PHONE_E164}`} className="font-medium underline">
            {BUSINESS_PHONE_DISPLAY}
          </a>
          .
        </p>
      </>,
    );
  }

  return frame(
    `Your ${noun} is saved.`,
    <>
      <p className="mt-2 text-base">{standing}</p>
      {stage === 'pause_lifted' ? (
        <p className="mt-3 text-base">
          The office lifted the pause on paying this one by Zelle. To pay it by Zelle after all,
          call or text the office at {BUSINESS_PHONE_DISPLAY} first, so your {noun} is not charged
          as well.
        </p>
      ) : (
        <>
          <p className="mt-3 text-base">
            Would you rather pay this one by Zelle? Tell us first, and we will not charge your{' '}
            {noun} for this invoice.
          </p>
          {refused ? (
            <div className="mt-3">
              <ErrorNotice title="That did not go" message={refused} />
            </div>
          ) : null}
          <div className="mt-3">
            <Button tone="quiet" onClick={() => void payingByZelle()} disabled={pressing}>
              {pressing ? 'One moment' : "I'm paying by Zelle"}
            </Button>
          </div>
        </>
      )}
    </>,
  );
}

function StripeScreen({
  headline,
  line,
  quiet,
  extra,
  action,
  step,
  onPress,
  secondary,
  back,
}: {
  headline: string;
  line: string;
  /** A rank quieter, beneath the office being in touch, once she said something was not right. */
  quiet: boolean;
  /** What the screen offers above its button: her own tick on the bank screen, or nothing. */
  extra: ReactNode;
  action: string;
  step: AskStep;
  onPress: () => void;
  secondary: { readonly words: string; readonly onPress: () => void } | null;
  back: { readonly words: string; readonly onPress: () => void };
}) {
  const offered =
    step.at === 'idle' || step.at === 'asking' || step.at === 'slow' || step.at === 'failed';
  return (
    <div>
      <ScreenHeading quiet={quiet}>{headline}</ScreenHeading>
      <p className="mt-2 text-base">{line}</p>
      {extra}

      <div className="mt-4 space-y-3">
        {step.at === 'making' ? (
          <p className="text-sm" role="status">
            Stripe is getting your page ready.
          </p>
        ) : null}
        {step.at === 'slow' ? (
          <p className="text-sm">Stripe is taking longer than usual. Try again in a minute.</p>
        ) : null}
        {step.at === 'going' ? (
          <p className="text-sm" role="status">
            Taking you to Stripe.{' '}
            <a href={step.url} className="font-medium underline">
              Go to Stripe
            </a>
          </p>
        ) : null}
        {step.at === 'stopped' ? <p className="text-sm">{step.message}</p> : null}
        {step.at === 'failed' ? (
          <ErrorNotice title="Stripe was not reached" message={step.message} />
        ) : null}
        {offered ? (
          <Button full onClick={onPress} disabled={step.at === 'asking'}>
            {step.at === 'asking' ? 'One moment' : action}
          </Button>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-navy/10 pt-4">
        {secondary ? (
          <Button tone="quiet" onClick={secondary.onPress}>
            {secondary.words}
          </Button>
        ) : null}
        <button type="button" className={LINK_BUTTON} onClick={back.onPress}>
          {back.words}
        </button>
      </div>
    </div>
  );
}

function Report({ artifact }: { artifact: Extract<PublicArtifact, { kind: 'visit_report' }> }) {
  return (
    <section className="rounded-xl border border-navy/12 bg-parchment p-5">
      <h1 className="font-serif text-2xl leading-tight">Your visit</h1>
      {artifact.visitDate ? (
        <p className="mt-1 text-sm text-navy/70">{longDay(artifact.visitDate)}</p>
      ) : null}
      <h2 className="mt-4 text-xs tracking-wide text-navy/55 uppercase">What was done</h2>
      <p className="mt-1 text-base whitespace-pre-line">{artifact.whatWasDone}</p>
      {artifact.clientAskedFor ? (
        <>
          <h2 className="mt-4 text-xs tracking-wide text-navy/55 uppercase">You asked for</h2>
          <p className="mt-1 text-base whitespace-pre-line">{artifact.clientAskedFor}</p>
        </>
      ) : null}
      <p className="mt-5 text-xs text-navy/60">Written {formatInstant(artifact.submittedAt)}.</p>
      <p className="mt-3 text-sm text-navy/70">
        Thank you for having us. If anything was missed, call or text the office and we will come
        back.
      </p>
    </section>
  );
}
